import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Flag, CheckCircle, ChatText, PaperPlaneTilt,
  ArrowCounterClockwise, UsersThree, User, Question,
} from "@phosphor-icons/react";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { cn } from "@/lib/utils";
import { api } from "../api";

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const OUTCOME_LABEL = {
  not_a_word:    "Word not recognised",
  inappropriate: "Flagged as inappropriate content",
  error:         "Generation error",
  unknown:       "Reason unknown",
};

/* ── Flag card ───────────────────────────────────────────── */
function FlagCard({ flag, onResolved }) {
  const isWordQuery = flag.type === "parent_word_query";

  const [adminNote,   setAdminNote]   = useState("");
  const [reapprove,   setReapprove]   = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");
  const [expanded,    setExpanded]    = useState(true);

  // child_signal uses "escalated"; parent_word_query uses "pending_admin"
  const needsAction = isWordQuery
    ? flag.status === "pending_admin"
    : flag.status === "escalated";

  const resolve = async (action) => {
    setSaving(true); setError("");
    try {
      await api("/admin/flags/resolve", {
        method: "POST",
        body: {
          notification_id: flag.notification_id,
          action,
          admin_note:     adminNote.trim() || null,
          reapprove_word: isWordQuery ? false : reapprove,
        },
      });
      onResolved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border bg-card shadow-soft">

      {/* Header */}
      <button className="flex w-full items-start gap-3 p-4 text-left"
        onClick={() => setExpanded((e) => !e)}>
        <span className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          isWordQuery ? "bg-primary/12" : "bg-warning/15",
        )}>
          {isWordQuery
            ? <Question size={15} weight="fill" className="text-primary-strong" />
            : <Flag     size={15} weight="fill" className="text-warning-ink" />
          }
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-extrabold text-foreground">
            {isWordQuery
              ? <>Word &ldquo;{flag.word}&rdquo; requested by {flag.parent_name || "Parent"}</>
              : <>&ldquo;{flag.word}&rdquo; — flagged by {flag.child_name}</>
            }
          </p>
          <p className="mt-0.5 text-[11px] font-semibold text-muted-foreground">
            {timeAgo(flag.created_at)}
            {needsAction && (
              <BloomBadge
                variant={isWordQuery ? "primary" : "warning"}
                className="ml-2 px-2 py-0 text-[9px]">
                {isWordQuery ? "Query pending" : "Needs review"}
              </BloomBadge>
            )}
          </p>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">

          {/* ── Word query: error the parent saw ── */}
          {isWordQuery && (
            <div className="rounded-xl bg-primary/8 px-3 py-2.5">
              <p className="text-[10px] font-extrabold uppercase tracking-wide text-primary-strong">
                Error the parent saw
              </p>
              <p className="mt-0.5 text-sm font-semibold text-foreground">
                {flag.error_detail
                  || OUTCOME_LABEL[flag.outcome]
                  || "No error detail captured"}
              </p>
              {flag.outcome && flag.outcome !== "unknown" && (
                <BloomBadge variant="primary" className="mt-1.5 px-2 py-0.5 text-[10px]">
                  {OUTCOME_LABEL[flag.outcome] || flag.outcome}
                </BloomBadge>
              )}
            </div>
          )}

          {/* ── Child signal: child's reason ── */}
          {!isWordQuery && (
            <div className="rounded-xl bg-warning/10 px-3 py-2.5">
              <p className="text-[10px] font-extrabold uppercase tracking-wide text-warning-ink">
                {flag.child_name} said
              </p>
              <p className="mt-0.5 text-sm font-semibold text-foreground">
                &ldquo;{flag.child_reason}&rdquo;
              </p>
            </div>
          )}

          {/* ── Parent note / message (both types) ── */}
          {flag.parent_note && (
            <div className="rounded-xl bg-muted/60 px-3 py-2.5">
              <p className="text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground">
                {isWordQuery ? "Parent's message" : "Parent's note"}
              </p>
              <p className="mt-0.5 text-sm font-semibold text-foreground">{flag.parent_note}</p>
            </div>
          )}

          {/* ── Resolution actions ── */}
          {needsAction && (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">
                  {isWordQuery ? "Your reply to parent (optional)" : "Your note to parent (optional)"}
                </label>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={2}
                  placeholder={isWordQuery
                    ? "e.g. We've added this word to our review queue and will update you soon…"
                    : "e.g. We've simplified the meaning for Year 4…"
                  }
                  className="mt-1.5 w-full resize-none rounded-xl border border-border bg-muted/40 px-3 py-2 text-sm font-semibold text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none"
                />
              </div>

              {/* Re-approve checkbox — child signals only (word doesn't exist yet for queries) */}
              {!isWordQuery && (
                <label className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-foreground">
                  <input type="checkbox" checked={reapprove}
                    onChange={(e) => setReapprove(e.target.checked)}
                    className="h-4 w-4 rounded accent-primary" />
                  Re-approve word after resolving (restore to live)
                </label>
              )}

              {error && <p className="text-xs font-bold text-destructive">{error}</p>}

              <div className="flex flex-wrap gap-2">
                <BloomButton size="sm" leftIcon={CheckCircle} disabled={saving}
                  onClick={() => resolve("resolve")}>
                  {saving ? "Saving…" : isWordQuery ? "Respond & close" : "Resolve & notify parent"}
                </BloomButton>
                <BloomButton size="sm" variant="outline" disabled={saving}
                  onClick={() => resolve("dismiss")}>
                  {isWordQuery ? "Dismiss query" : "Dismiss (restore word)"}
                </BloomButton>
              </div>
            </div>
          )}

          {/* ── Resolved state ── */}
          {flag.status === "resolved" && (
            <div className="rounded-xl bg-teal/10 px-3 py-2">
              <p className="text-[10px] font-extrabold uppercase tracking-wide text-teal-ink">Resolved</p>
              {flag.admin_note && <p className="mt-0.5 text-sm font-semibold text-foreground">{flag.admin_note}</p>}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

/* ── Message composer ────────────────────────────────────── */
function MessageComposer({ parents, onSent }) {
  const [msg,      setMsg]      = useState("");
  const [target,   setTarget]   = useState("all"); // "all" | parent_id
  const [childId,  setChildId]  = useState("");
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  const send = async () => {
    if (!msg.trim()) return;
    setSaving(true); setError(""); setSuccess("");
    try {
      const body = {
        message:      msg.trim(),
        to_parent_id: target === "all" ? null : target,
        to_child_id:  childId || null,
      };
      const res = await api("/admin/messages", { method: "POST", body });
      setSuccess(`Sent to ${res.sent_to} parent${res.sent_to !== 1 ? "s" : ""} ✓`);
      setMsg("");
      onSent();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const selectedParent = parents.find((p) => p.user_id === target);

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <p className="font-display text-sm font-black text-foreground">Send a message</p>
      <p className="mt-0.5 text-xs font-semibold text-muted-foreground">
        Compose a personal notification — emojis welcome 🎉
      </p>

      {/* Target selector */}
      <div className="mt-4 space-y-2">
        <label className="text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">Send to</label>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setTarget("all"); setChildId(""); }}
            className={cn(
              "flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-extrabold transition",
              target === "all" ? "border-primary bg-accent text-primary-strong" : "border-border text-muted-foreground hover:border-primary/40",
            )}>
            <UsersThree size={13} /> All parents
          </button>
          {parents.map((p) => (
            <button key={p.user_id} onClick={() => { setTarget(p.user_id); setChildId(""); }}
              className={cn(
                "flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-extrabold transition",
                target === p.user_id ? "border-primary bg-accent text-primary-strong" : "border-border text-muted-foreground hover:border-primary/40",
              )}>
              <User size={13} /> {p.name || p.email}
            </button>
          ))}
        </div>

        {/* Optional child filter */}
        {target !== "all" && selectedParent?.children?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <p className="w-full text-[10px] font-semibold text-muted-foreground">About which child? (optional)</p>
            {selectedParent.children.map((ch) => (
              <button key={ch.child_id} onClick={() => setChildId(ch.child_id === childId ? "" : ch.child_id)}
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11px] font-bold transition",
                  childId === ch.child_id ? "border-primary bg-accent text-primary-strong" : "border-border text-muted-foreground",
                )}>
                {ch.first_name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Message box */}
      <textarea
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        rows={3}
        maxLength={500}
        placeholder="e.g. We've added 12 new Year 6 words this week 📚"
        className="mt-3 w-full resize-none rounded-xl border border-border bg-muted/40 px-3 py-2.5 text-sm font-semibold text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none"
      />
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground">{msg.length}/500</span>
      </div>

      {error   && <p className="mt-1 text-xs font-bold text-destructive">{error}</p>}
      {success && <p className="mt-1 text-xs font-bold text-teal-ink">{success}</p>}

      <BloomButton size="sm" leftIcon={PaperPlaneTilt} className="mt-3"
        disabled={saving || !msg.trim()} onClick={send}>
        {saving ? "Sending…" : "Send notification"}
      </BloomButton>
    </div>
  );
}

/* ── Sent messages history ───────────────────────────────── */
function SentMessages({ messages }) {
  if (!messages.length) return null;
  return (
    <div className="space-y-2">
      <p className="font-display text-sm font-black text-foreground">Sent messages</p>
      {messages.map((m, i) => (
        <div key={i} className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{m.message}</p>
            <p className="mt-0.5 text-[11px] font-semibold text-muted-foreground">
              {timeAgo(m.created_at)}
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-xs font-extrabold text-foreground">{m.acknowledged} / {m.total}</p>
            <p className="text-[10px] text-muted-foreground">acknowledged</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Main AdminFlags page ────────────────────────────────── */
export default function AdminFlags() {
  const [flags,    setFlags]    = useState([]);
  const [messages, setMessages] = useState([]);
  const [parents,  setParents]  = useState([]);
  const [tab,      setTab]      = useState("flags"); // "flags" | "messages"
  const [filter,   setFilter]   = useState("needs_review");
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let flagsData;
      if (filter === "needs_review") {
        // Combine escalated child signals + pending_admin word queries into one list
        const [escalated, wordQueries] = await Promise.all([
          api("/admin/flags?status=escalated"),
          api("/admin/flags?status=pending_admin"),
        ]);
        flagsData = [...escalated, ...wordQueries].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
      } else {
        flagsData = await api(`/admin/flags?status=${filter}`);
      }
      const [m, p] = await Promise.all([
        api("/admin/messages"),
        api("/admin/parents"),
      ]);
      setFlags(flagsData);
      setMessages(m);
      setParents(p);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-flags">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight text-foreground">
            Flags & Messages
          </h1>
          <p className="mt-1 text-sm font-semibold text-muted-foreground">
            Review parent-escalated flags, word queries, and send notifications.
          </p>
        </div>
        <button onClick={load} aria-label="Refresh"
          className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-2 text-xs font-bold text-muted-foreground hover:bg-border">
          <ArrowCounterClockwise size={13} weight="bold" /> Refresh
        </button>
      </div>

      {/* Tab switcher */}
      <div className="mt-5 flex gap-1 rounded-2xl bg-muted/60 p-1.5 w-fit">
        {[["flags", "🚩 Flags & Queries"], ["messages", "💬 Messages"]].map(([val, label]) => (
          <button key={val} onClick={() => setTab(val)}
            className={cn(
              "rounded-xl px-4 py-2 text-sm font-extrabold transition",
              tab === val ? "bg-card text-foreground shadow-soft" : "text-muted-foreground hover:text-foreground",
            )}>
            {label}
          </button>
        ))}
      </div>

      {/* Flags tab */}
      {tab === "flags" && (
        <div className="mt-5 space-y-4">
          {/* Filter */}
          <div className="flex flex-wrap gap-2">
            {[
              ["needs_review", "Needs review"],
              ["resolved",     "Resolved"],
              ["dismissed",    "Dismissed"],
              ["all",          "All"],
            ].map(([val, label]) => (
              <button key={val} onClick={() => setFilter(val)}
                className={cn(
                  "rounded-full border-2 px-3 py-1 text-xs font-extrabold transition",
                  filter === val ? "border-primary bg-accent text-primary-strong" : "border-border text-muted-foreground hover:border-primary/40",
                )}>
                {label}
              </button>
            ))}
          </div>

          {loading && <div className="space-y-3">{[1,2].map((i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-muted"/>)}</div>}

          {!loading && flags.length === 0 && (
            <div className="py-12 text-center">
              <Flag size={36} weight="thin" className="mx-auto text-muted-foreground/40" />
              <p className="mt-3 text-sm font-semibold text-muted-foreground">
                {filter === "needs_review" ? "No flags or word queries waiting for review 🎉" : "Nothing here yet"}
              </p>
            </div>
          )}

          {!loading && flags.map((f) => (
            <FlagCard key={f.notification_id} flag={f} onResolved={load} />
          ))}
        </div>
      )}

      {/* Messages tab */}
      {tab === "messages" && (
        <div className="mt-5 space-y-5">
          <MessageComposer parents={parents} onSent={load} />
          <SentMessages messages={messages} />
        </div>
      )}
    </div>
  );
}
